/*
  Search results groups.

  The search page renders results in two lists: in-store "ReStore finds" and the special-order
  cabinet catalog (products tagged "Special Order"). This element turns them into tabs, pulls
  in-store finds forward from the following result pages so the first screen is not all
  cabinets, and replaces pagination with a Load more button. Without JavaScript both lists and
  the normal pagination still render.

  Collection pages (main-collection-product-grid) use the same element for department
  collections, with data-noun="products" and their own data-remember-key.

  The element is re-created whenever facets.js replaces #ProductGridContainer, so every
  connectedCallback starts from fresh server-rendered markup.
*/
if (!customElements.get('search-results-groups')) {
  customElements.define(
    'search-results-groups',
    class SearchResultsGroups extends HTMLElement {
      static MIN_RESTORE_ON_FIRST_SCREEN = 12;
      static MAX_AUTO_PAGES = 3;
      static MAX_PAGES_PER_CLICK = 5;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
        this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
        this.totalResults = parseInt(this.dataset.totalResults, 10) || 0;
        this.noun = this.dataset.noun || 'results';
        this.rememberKey = this.dataset.rememberKey || 'search-results-group';
        this.autoPagesLoaded = 0;
        this.loading = false;

        this.tablist = this.querySelector('.search-groups__tabs');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.lists = {
          restore: this.querySelector('[data-group-list="restore"]'),
          special: this.querySelector('[data-group-list="special"]'),
        };
        this.loadMore = this.querySelector('[data-load-more]');
        this.progress = this.querySelector('[data-progress]');
        this.note = this.querySelector('[data-note]');
        this.empty = this.querySelector('[data-empty]');

        this.classList.add('search-groups--enhanced');
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.openTab(tab.dataset.group));
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
        });
        if (this.loadMore) this.loadMore.addEventListener('click', () => this.loadMoreForSelected());

        let remembered = null;
        try {
          remembered = sessionStorage.getItem(this.rememberKey);
        } catch (e) {
          remembered = null;
        }
        this.select(remembered === 'special' && this.count('special') > 0 ? 'special' : 'restore', false);
        this.refresh();
        this.autoFill();
      }

      count(group) {
        return this.lists[group] ? this.lists[group].querySelectorAll('li[data-group]').length : 0;
      }

      get selected() {
        const tab = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
        return tab ? tab.dataset.group : 'restore';
      }

      select(group, remember) {
        if (!this.lists[group]) return;
        this.tabs.forEach((tab) => {
          const active = tab.dataset.group === group;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });
        Object.entries(this.lists).forEach(([name, list]) => {
          if (list) (list.closest('[data-group-panel]') || list).hidden = name !== group;
        });
        if (remember) {
          try {
            sessionStorage.setItem(this.rememberKey, group);
          } catch (e) {
            /* storage unavailable */
          }
        }
        this.refresh();
      }

      onTabKeydown(event, tab) {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const index = this.tabs.indexOf(tab);
        const next = this.tabs[(index + (event.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length];
        next.focus();
        this.openTab(next.dataset.group);
      }

      // A shopper picked a tab: show it, then fill it if it holds only a few items so far.
      openTab(group) {
        this.select(group, true);
        this.autoPagesLoaded = 0;
        this.autoFill();
      }

      // Counts, empty state, tab visibility and the Load more / progress line.
      refresh() {
        const restore = this.count('restore');
        const special = this.count('special');
        const morePages = this.currentPage < this.totalPages;
        // "47+" while more result pages remain, so a tab showing 1 doesn't read as "only 1 exists".
        this.querySelectorAll('[data-count]').forEach((el) => {
          el.textContent = `${el.dataset.count === 'special' ? special : restore}${morePages ? '+' : ''}`;
        });
        if (this.tablist) this.tablist.hidden = special === 0;
        if (special === 0 && this.selected !== 'restore') this.select('restore', false);

        const loaded = restore + special;
        if (this.loadMore) this.loadMore.hidden = !morePages;
        if (this.progress) {
          this.progress.textContent = this.totalResults
            ? `Showing ${Math.min(loaded, this.totalResults)} of ${this.totalResults} ${this.noun}${morePages ? '' : '.'}`
            : '';
        }
        if (this.empty) {
          this.empty.hidden = !(this.selected === 'restore' && restore === 0 && !morePages);
        }
        if (this.note) {
          const showNote = this.selected === 'restore' && special > 0 && morePages && restore < SearchResultsGroups.MIN_RESTORE_ON_FIRST_SCREEN;
          this.note.textContent = showNote ? 'Most matches so far are special-order cabinets. Load more results to find items in our stores.' : '';
          this.note.hidden = !showNote;
        }
      }

      // Pull the next pages in until the open tab has a reasonable number of items on the first
      // screen (both tabs: sorted newest first, the special-order tab can open with a single item).
      autoFill() {
        if (this.count('special') === 0) return;
        const group = this.selected;
        if (this.count(group) >= SearchResultsGroups.MIN_RESTORE_ON_FIRST_SCREEN) return;
        if (this.autoPagesLoaded >= SearchResultsGroups.MAX_AUTO_PAGES || this.currentPage >= this.totalPages) {
          const other = group === 'restore' ? 'special' : 'restore';
          if (this.count(group) === 0 && this.count(other) > 0) this.select(other, false);
          return;
        }
        this.autoPagesLoaded += 1;
        this.fetchNextPage(true).then(() => this.autoFill());
      }

      // Load more: a result page can hold nothing for the open tab (sorted newest first, a whole page
      // can be special-order cabinets), so keep fetching until the open tab gets new items, up to
      // MAX_PAGES_PER_CLICK pages. Without this the button looked like it did nothing.
      loadMoreForSelected() {
        if (this.loading) return;
        const group = this.selected;
        const before = this.count(group);
        let pages = 0;
        this.setSpinner(true);
        const step = () =>
          this.fetchNextPage(true).then(() => {
            pages += 1;
            if (this.count(group) === before && this.currentPage < this.totalPages && pages < SearchResultsGroups.MAX_PAGES_PER_CLICK) {
              return step();
            }
          });
        step().finally(() => this.setSpinner(false));
      }

      setSpinner(on) {
        if (!this.loadMore) return;
        this.loadMore.classList.toggle('loading', on);
        this.loadMore.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
      }

      pageUrl(page) {
        const params = new URLSearchParams(window.location.search);
        params.set('page', page);
        params.set('section_id', this.sectionId);
        return `${window.location.pathname}?${params.toString()}`;
      }

      fetchNextPage(silent) {
        if (this.loading || this.currentPage >= this.totalPages) return Promise.resolve();
        this.loading = true;
        const nextPage = this.currentPage + 1;
        if (!silent) this.setSpinner(true);
        return fetch(this.pageUrl(nextPage))
          .then((response) => response.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const incoming = doc.querySelector('search-results-groups');
            if (!incoming) return;
            ['restore', 'special'].forEach((group) => {
              const target = this.lists[group];
              if (!target) return;
              incoming.querySelectorAll(`[data-group-list="${group}"] > li[data-group]`).forEach((li) => {
                li.classList.remove('scroll-trigger', 'animate--slide-in');
                target.appendChild(li);
              });
            });
            this.currentPage = nextPage;
            this.dataset.currentPage = String(nextPage);
          })
          .catch(() => {
            /* leave the button available for another try */
          })
          .finally(() => {
            this.loading = false;
            if (!silent) this.setSpinner(false);
            this.refresh();
          });
      }
    }
  );
}
